'use client'

import { useResetSmartPreferences, useSRPAIFilterManager } from '@/src/hooks/srp/aiRecommendation'

import useSearchResults from '@/src/hooks/srp/useSearchResults'
import {
    selectAIErrorCode,
    selectAILastSearchedInput,
    selectAILoading,
    selectAITextInput,
    setTextInput,
    type JourneyLeg
} from '@/src/redux/slices/aiRecommendationSlice'
import { useAppDispatch, useAppSelector } from '@/src/redux/store'
import { smartSRPMessages } from '@/src/utils/srp/aiRecommendation/locale'
import AutoAwesome from '@mui/icons-material/AutoAwesome'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useLocale } from 'next-intl'
import { useCallback, useMemo } from 'react'
import TextModeTab from './TextModeTab'

const AIFilterSidebar = () => {
    const locale = useLocale() as 'en' | 'ar'
    const smartSRPMessagesData = useMemo(() => smartSRPMessages[locale], [locale])
    const dispatch = useAppDispatch()
    const { selectedJourneyLeg } = useSearchResults()
    const leg = selectedJourneyLeg as JourneyLeg

    const textInput = useAppSelector(state => selectAITextInput(state, leg))
    const lastSearchedInput = useAppSelector(state => selectAILastSearchedInput(state, leg))
    const loading = useAppSelector(state => selectAILoading(state, leg))
    const errorCode = useAppSelector(state => selectAIErrorCode(state, leg))
    const errorMessage = errorCode ? smartSRPMessagesData.errors[errorCode] : null

    const { applySmartAiFilters } = useSRPAIFilterManager()
    const { resetSmartPreferences, hasActivePreferences } = useResetSmartPreferences()

    const trimmedInput = textInput.trim()
    const isSameAsLastSearch = trimmedInput === lastSearchedInput
    const isButtonDisabled = loading || !trimmedInput || isSameAsLastSearch

    const handleTextChange = useCallback(
        (value: string) => {
            dispatch(setTextInput({ leg, value }))
        },
        [dispatch, leg]
    )

    const handleFilterFlights = useCallback(async () => {
        await applySmartAiFilters(textInput)
    }, [applySmartAiFilters, textInput])

    const theme = useTheme()

    return (
        <Box sx={{ p: 1.5 }} id="ai-search-sidebar">
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.85)} 0%, ${alpha(theme.palette.secondary.main, 0.7)} 50%, ${alpha(theme.palette.primary.main, 0.85)} 100%)`,
                    borderRadius: 2,
                    p: '1.5px',
                }}
            >
                <Box
                    sx={{
                        background: theme.palette.background.paper,
                        borderRadius: 1.85,
                        px: 1,
                        py: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AutoAwesome sx={{ fontSize: 18, color: 'primary.main' }} />
                            <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'text.primary' }}>
                                {smartSRPMessagesData.smartSearchTitle}
                            </Typography>
                            <Tooltip title={smartSRPMessagesData.smartSearchDescription} arrow placement="top">
                                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer' }} />
                            </Tooltip>
                        </Box>

                        {hasActivePreferences && (
                            <Button
                                variant="text"
                                size="small"
                                onClick={resetSmartPreferences}
                            >
                                {smartSRPMessagesData.smartSearchResetButton}
                            </Button>
                        )}
                    </Box>

                    <TextModeTab
                        value={textInput}
                        onChange={handleTextChange}
                    />

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleFilterFlights}
                        disabled={isButtonDisabled}
                        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {loading ? smartSRPMessagesData.smartSearchButtonLoading : smartSRPMessagesData.smartSearchButton}
                    </Button>

                    {errorMessage && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {errorMessage}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

export default AIFilterSidebar
